import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { FiArrowRight, FiMapPin, FiStar, FiUsers, FiAward, FiChevronLeft, FiChevronRight, FiCamera, FiHeart, FiPhone } from 'react-icons/fi';
import { GiMountainCave, GiPalmTree, GiSnowflake2, GiSunCloud } from 'react-icons/gi';
import { MdOutlineExplore, MdSecurity, MdGroups } from 'react-icons/md';
import API from '../utils/api';
import TripCard from '../components/TripCard';
import { HERO_IMGS, WHY_IMGS, DEST_IMAGES } from '../utils/images';
import './Home.css';

const HERO_IMAGES = HERO_IMGS;

// eslint-disable-next-line no-unused-vars
const HERO_LABELS = [
  { place: 'Sangam Point, Ladakh', country: 'India' },
  { place: 'Sonamarg, Kashmir', country: 'India' },
  { place: 'Dawki River, Meghalaya', country: 'India' },
  { place: 'Backwaters, Kerala', country: 'India' },
];

const WHY_IMAGES = WHY_IMGS;

const DESTINATIONS = [
  { name: 'Ladakh',      tagline: 'Land of High Passes',  image: DEST_IMAGES.ladakh,    icon: <GiSnowflake2 /> },
  { name: 'Kashmir',     tagline: 'Paradise on Earth',    image: DEST_IMAGES.kashmir,   icon: <GiSunCloud /> },
  { name: 'Meghalaya',   tagline: 'Abode of Clouds',      image: DEST_IMAGES.meghalaya, icon: <GiMountainCave /> },
  { name: 'Kerala',      tagline: "God's Own Country",    image: DEST_IMAGES.kerala,    icon: <GiPalmTree /> },
  { name: 'Spiti Valley',tagline: 'The Middle Land',      image: DEST_IMAGES.spiti,     icon: <GiMountainCave /> },
  { name: 'Sri Lanka',   tagline: 'Pearl of the Orient',  image: DEST_IMAGES.srilanka,  icon: <GiPalmTree /> },
];
const TESTIMONIALS = [
  {
    name: 'Nikhitha B.S.',
    text: 'It was an amazing experience exploring Meghalaya with The Unexplored routes. Manik was a very good host and took care of all the arrangements so well. Driver was friendly and co-operative. Overall we enjoyed a lot in this trip.',
    rating: 5,
    trip: 'Meghalaya Expedition',
    avatar: 'N',
  },
  {
    name: 'Anirudh Bharadwaj',
    text: 'Excellent planning and execution by Manik. The trip was reasonably priced and Manik checked up on us every day to ensure our comfort. I would urge everyone to visit Meghalaya once. Consider The Unexplored Routes — you\'ll come back happy and filled with memories.',
    rating: 5,
    trip: 'Meghalaya Adventure',
    avatar: 'A',
  },
  {
    name: 'Prasad Allena',
    text: 'We — a group of 6 friends — visited Arunachal Pradesh. It was a great experience. Manik made sure that the driver was completely aware of our plan, stays, and places to visit. We covered all the places as promised. I recommend The Unexplored Routes especially Manik for all your travels without a second thought.',
    rating: 5,
    trip: 'Arunachal Pradesh Tour',
    avatar: 'P',
  },
  {
    name: 'Amal Babu',
    text: 'We had a great trip across Meghalaya with The Unexplored Routes. When there was rain and some of us felt uncomfortable with the tents, Manik quickly arranged rooms for us. His quick response and problem-solving ability is impressive.',
    rating: 5,
    trip: 'Meghalaya Exploration',
    avatar: 'A',
  },
  {
    name: 'Kandan Log',
    text: 'It was a really good experience with Unexplored Group. Manik was super helpful throughout the trip. All the arrangements were up to the mark and we did not face any issues. The driver was very humble and ensured we enjoyed amazing food in Meghalaya.',
    rating: 5,
    trip: 'Meghalaya Tour',
    avatar: 'K',
  },
];

const WHY_US = [
  { icon: <MdOutlineExplore />, title: 'Offbeat Destinations', desc: 'We specialize in unique, less-travelled routes that offer authentic local experiences away from tourist crowds.' },
  { icon: <MdSecurity />, title: 'Safe & Secure', desc: 'Your safety is our top priority. All trips are carefully planned with experienced local guides and reliable transport.' },
  { icon: <MdGroups />, title: 'Small Groups', desc: 'Intimate group sizes ensure personalized attention, better bonding, and a more meaningful travel experience.' },
  { icon: <FiAward />, title: 'Best Value', desc: 'Premium experiences at honest prices. No hidden charges — everything is transparent from the start.' },
];

const STATS = [
  { value: '500+', label: 'Happy Travelers' },
  { value: '50+', label: 'Destinations' },
  { value: '5+', label: 'Years Experience' },
  { value: '4.9', label: 'Average Rating' },
];

export default function Home() {
  const [heroIndex, setHeroIndex] = useState(0);
  const [whyImgIndex, setWhyImgIndex] = useState(0);
  const [trips, setTrips] = useState([]);
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const heroRef = useRef(null);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 200]);
  const heroOpacity = useTransform(scrollY, [0, 400], [1, 0]);

  // Hero auto-play
  useEffect(() => {
    const timer = setInterval(() => setHeroIndex(i => (i + 1) % HERO_IMAGES.length), 5000);
    return () => clearInterval(timer);
  }, []);

  // Why-Us image auto-cycle
  useEffect(() => {
    const timer = setInterval(() => setWhyImgIndex(i => (i + 1) % WHY_IMAGES.length), 3500);
    return () => clearInterval(timer);
  }, []);

  // Testimonials auto-play
  useEffect(() => {
    const timer = setInterval(() => setTestimonialIndex(i => (i + 1) % TESTIMONIALS.length), 6000);
    return () => clearInterval(timer);
  }, []);

  // Fetch featured trips
  useEffect(() => {
    const fetchTrips = async () => {
      try {
        const res = await API.get('/api/trips?featured=true');
        setTrips(res.data.data?.slice(0, 3) || []);
      } catch {
        setTrips([]);
      }
    };
    fetchTrips();
  }, []);

  const fadeUp = {
    initial: { opacity: 0, y: 40 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-50px' },
    transition: { duration: 0.7, ease: 'easeOut' },
  };

  const stagger = {
    initial: { opacity: 0 },
    whileInView: { opacity: 1 },
    viewport: { once: true },
    transition: { staggerChildren: 0.12 },
  };

  const childFade = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.5 },
  };

  return (
    <div className="home">
      {/* ─── HERO ─── */}
      <section className="hero" ref={heroRef}>
        <div className="hero-slides">
          <AnimatePresence mode="sync">
            <motion.div
              key={heroIndex}
              className="hero-slide"
              style={{ backgroundImage: `url(${HERO_IMAGES[heroIndex]})`, y: heroY }}
              initial={{ opacity: 0, scale: 1.08 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.2 }}
            />
          </AnimatePresence>
        </div>
        <div className="hero-overlay" />

        {/* Location tag top-right */}
        {/* <AnimatePresence mode="wait">
          <motion.div
            key={`loc-${heroIndex}`}
            className="hero-location-tag"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.5 }}
          >
            <FiMapPin />
            <span>{HERO_LABELS[heroIndex].place}, {HERO_LABELS[heroIndex].country}</span>
          </motion.div>
        </AnimatePresence> */}

        <motion.div className="hero-content" style={{ opacity: heroOpacity }}>
          <motion.span
            className="hero-eyebrow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            ✦ An Offbeat Travel Agency ✦
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            Discover India's
            <br />
            <span className="hero-highlight">Hidden Wonders</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            Journey beyond the ordinary. Explore Ladakh, Kashmir, Meghalaya, Kerala, Spiti Valley &amp; Sri Lanka with India's most trusted offbeat travel agency.
          </motion.p>
          <motion.div
            className="hero-actions"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <Link to="/trips" className="btn-gold">Explore Trips <FiArrowRight /></Link>
            <motion.div
              className="hero-enquiry-wrap"
              animate={{ scale: [1, 1.04, 1] }}
              transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut', repeatDelay: 1.5 }}
            >
              <Link to="/contact" className="btn-hero-enquiry">
                <FiPhone /> Enquiry Now
              </Link>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Hero slide dots */}
        <div className="hero-dots">
          {HERO_IMAGES.map((_, i) => (
            <button
              key={i}
              className={`dot ${i === heroIndex ? 'active' : ''}`}
              onClick={() => setHeroIndex(i)}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="scroll-indicator"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
        >
          <div className="scroll-line" />
          <span>Scroll</span>
        </motion.div>
      </section>

      {/* ─── STATS BAR ─── */}
      <section className="stats-bar">
        <div className="container">
          <motion.div className="stats-grid" {...stagger}>
            {STATS.map((stat) => (
              <motion.div key={stat.label} className="stat-item" {...childFade}>
                <span className="stat-value">{stat.value}</span>
                <span className="stat-label">{stat.label}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ─── UPCOMING DESTINATIONS ─── */}
      <section className="destinations-section">
        <div className="container">
          <motion.div className="section-header" {...fadeUp}>
            <span className="section-subtitle">Where To Go</span>
            <h2 className="section-title">Upcoming Destinations</h2>
            <p className="section-desc">
              Handpicked offbeat destinations waiting to be explored. Each journey crafted for unforgettable memories.
            </p>
          </motion.div>

          <motion.div className="destinations-grid" {...stagger}>
            {DESTINATIONS.map((dest, i) => (
              <motion.div
                key={dest.name}
                className={`dest-card ${i === 0 ? 'large' : ''}`}
                {...childFade}
                whileHover={{ scale: 1.02 }}
              >
                <img src={dest.image} alt={dest.name} loading="lazy" onError={(e) => { e.target.src = '/ladakh.jpg'; }} />
                <div className="dest-overlay" />
                <div className="dest-content">
                  <div className="dest-icon">{dest.icon}</div>
                  <h3>{dest.name}</h3>
                  <p>{dest.tagline}</p>
                  <Link to="/trips" className="dest-link">Explore <FiArrowRight /></Link>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ─── FEATURED TRIPS ─── */}
      <section className="featured-section">
        <div className="container">
          <motion.div className="section-header" {...fadeUp}>
            <span className="section-subtitle">Top Picks</span>
            <h2 className="section-title">Featured Trips</h2>
            <p className="section-desc">
              Our most popular and highly-rated offbeat experiences — loved by hundreds of happy travelers.
            </p>
          </motion.div>

          {trips.length > 0 ? (
            <motion.div className="trips-grid" {...stagger}>
              {trips.map((trip) => (
                <motion.div key={trip._id} {...childFade}>
                  <TripCard trip={trip} />
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <div className="no-trips-placeholder">
              <div className="trips-preview-grid">
                {[
                  { title: 'Ladakh Adventure',       dest: 'Leh, Ladakh',            price: '18999', duration: '7N/8D', img: '/ladakh.jpg',           rating: 4.9 },
                  { title: 'Meghalaya Exploration',   dest: 'Shillong & Cherrapunji', price: '14999', duration: '5N/6D', img: '/meghalaya.jpg',     rating: 4.8 },
                  { title: 'Kashmir Valley',          dest: 'Srinagar, Kashmir',      price: '16999', duration: '6N/7D', img: '/kashmir.jpg',      rating: 4.9 },
                ].map((t) => (
                  <motion.div
                    key={t.title}
                    className="preview-card"
                    {...childFade}
                    whileHover={{ y: -6 }}
                  >
                    <div className="preview-img">
                      <img src={t.img} alt={t.title} loading="lazy" />
                      <div className="preview-overlay" />
                      <span className="preview-rating"><FiStar /> {t.rating}</span>
                    </div>
                    <div className="preview-body">
                      <div className="preview-dest"><FiMapPin /> {t.dest}</div>
                      <h3>{t.title}</h3>
                      <div className="preview-footer">
                        <div>
                          <span className="preview-duration">{t.duration}</span>
                          <p className="preview-price">₹{t.price}<span>/person</span></p>
                        </div>
                        <Link to="/contact" className="preview-btn">Book Now <FiArrowRight /></Link>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          <motion.div className="section-cta" {...fadeUp}>
            <Link to="/trips" className="btn-primary">View All Trips <FiArrowRight /></Link>
          </motion.div>
        </div>
      </section>

      {/* ─── WHY CHOOSE US ─── */}
      <section className="why-us-section">
        <div className="container">
          <div className="why-us-inner">
            <motion.div className="why-us-content" {...fadeUp}>
              <span className="section-subtitle">Why Choose Us</span>
              <h2 className="section-title">Travel Differently<br />With Us</h2>
              <p className="section-desc" style={{ marginBottom: '40px' }}>
                We're not just another travel agency. We're passionate explorers who believe every journey should tell a story — authentic, memorable, and truly yours.
              </p>
              <div className="why-features">
                {WHY_US.map((item, idx) => (
                  <motion.div
                    key={item.title}
                    className="why-feature"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: idx * 0.1 }}
                  >
                    <div className="feature-icon">{item.icon}</div>
                    <div>
                      <h4>{item.title}</h4>
                      <p>{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
              <Link to="/about" className="btn-primary" style={{ marginTop: '12px' }}>Learn More About Us <FiArrowRight /></Link>
            </motion.div>

            {/* ── Animated multi-image gallery ── */}
            <motion.div
              className="why-us-gallery"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              {/* Main large image — auto-cycling */}
              <div className="why-main-img-wrap">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={whyImgIndex}
                    src={WHY_IMAGES[whyImgIndex].src}
                    alt={WHY_IMAGES[whyImgIndex].alt}
                    className="why-main-img"
                    initial={{ opacity: 0, scale: 1.06 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.97 }}
                    transition={{ duration: 0.7 }}
                    onError={(e) => {
                      e.target.src = '/ladakh.jpg';
                    }}
                  />
                </AnimatePresence>
                {/* Label on image */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`lbl-${whyImgIndex}`}
                    className="why-img-label"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                  >
                    <FiCamera /> {WHY_IMAGES[whyImgIndex].label}
                  </motion.div>
                </AnimatePresence>
                {/* Dot indicators */}
                <div className="why-img-dots">
                  {WHY_IMAGES.map((_, i) => (
                    <button
                      key={i}
                      className={`why-dot ${i === whyImgIndex ? 'active' : ''}`}
                      onClick={() => setWhyImgIndex(i)}
                      aria-label={`Image ${i + 1}`}
                    />
                  ))}
                </div>
              </div>

              {/* Two small thumbnail images below */}
              <div className="why-thumb-row">
                {WHY_IMAGES.map((img, i) => (
                  <motion.div
                    key={i}
                    className={`why-thumb ${i === whyImgIndex ? 'active' : ''}`}
                    onClick={() => setWhyImgIndex(i)}
                    whileHover={{ scale: 1.04 }}
                    transition={{ duration: 0.2 }}
                  >
                    <img
                      src={img.src}
                      alt={img.alt}
                      onError={(e) => {
                        e.target.src = '/ladakh.jpg';
                      }}
                    />
                    {i === whyImgIndex && <div className="why-thumb-overlay-active" />}
                  </motion.div>
                ))}
              </div>

              {/* Floating stats badge */}
              <motion.div
                className="image-badge"
                animate={{ y: [0, -5, 0] }}
                transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
              >
                <FiUsers />
                <div>
                  <strong>500+</strong>
                  <span>Happy Travelers</span>
                </div>
              </motion.div>

              {/* Floating review badge */}
              <motion.div
                className="image-badge-2"
                animate={{ y: [0, 5, 0] }}
                transition={{ repeat: Infinity, duration: 3.5, ease: 'easeInOut', delay: 0.5 }}
              >
                <FiHeart />
                <div>
                  <strong>4.9 ★</strong>
                  <span>Avg Rating</span>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── TESTIMONIALS ─── */}
      <section className="testimonials-section">
        <div className="container">
          <motion.div className="section-header" {...fadeUp}>
            <span className="section-subtitle">Traveler Stories</span>
            <h2 className="section-title">What Our Travelers Say</h2>
          </motion.div>

          <div className="testimonials-slider">
            <AnimatePresence mode="wait">
              <motion.div
                key={testimonialIndex}
                className="testimonial-card"
                initial={{ opacity: 0, x: 60 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -60 }}
                transition={{ duration: 0.5 }}
              >
                <div className="stars">
                  {Array.from({ length: TESTIMONIALS[testimonialIndex].rating }).map((_, i) => (
                    <FiStar key={i} className="star-filled" />
                  ))}
                </div>
                <p className="testimonial-text">"{TESTIMONIALS[testimonialIndex].text}"</p>
                <div className="testimonial-author">
                  <div className="author-avatar">{TESTIMONIALS[testimonialIndex].avatar}</div>
                  <div>
                    <strong>{TESTIMONIALS[testimonialIndex].name}</strong>
                    <span>{TESTIMONIALS[testimonialIndex].trip}</span>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="slider-controls">
              <button onClick={() => setTestimonialIndex(i => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)} aria-label="Previous">
                <FiChevronLeft />
              </button>
              <div className="slider-dots">
                {TESTIMONIALS.map((_, i) => (
                  <button
                    key={i}
                    className={`dot ${i === testimonialIndex ? 'active' : ''}`}
                    onClick={() => setTestimonialIndex(i)}
                    aria-label={`Testimonial ${i + 1}`}
                  />
                ))}
              </div>
              <button onClick={() => setTestimonialIndex(i => (i + 1) % TESTIMONIALS.length)} aria-label="Next">
                <FiChevronRight />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── CTA BANNER ─── */}
      <section className="cta-section">
        <div className="cta-bg" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1493514789931-586cb221d7a7?w=1920&h=600&fit=crop)' }} />
        <div className="cta-overlay" />
        <div className="container">
          <motion.div className="cta-content" {...fadeUp}>
            <span className="section-subtitle" style={{ color: 'var(--secondary-light)' }}>Ready to Explore?</span>
            <h2>Your Next Adventure<br />Awaits</h2>
            <p>Contact us today and let's plan your perfect offbeat journey. We handle everything — you just bring the spirit of adventure.</p>
            <div className="cta-actions">
              <Link to="/contact" className="btn-gold">Plan Your Trip <FiArrowRight /></Link>
              <a href="tel:+918109849232" className="btn-outline-white">Call Us Now</a>
            </div>
            <div className="cta-contact">
              <span>📞 +91 81098 49232</span>
              <span>✉️ hello@theunexploredroutes.com</span>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
